import type { ReactNode } from 'react'
import { Button, Chip } from '@heroui/react'
import { counterFacts } from '../lib/counterFields'
import { serviceLabel } from '../lib/plan'
import { isKnownLanguage } from '../lib/i18n/languages'
import { formatDuration, formatMoment, languageName } from '../lib/staffText'
import type { QueueView, Service, Ticket, VisitField } from '../lib/types'
import { useStaffText } from '../ui/StaffText'

/** Six tile colours (ui-tokens --tile-n), handed out by queue order. */
export const TILE_COUNT = 6

/** A queue's tile colour, 1 to 6, from its place in the branch's queue order. */
export function tileOf(queues: readonly Pick<QueueView, 'queueId' | 'sortOrder'>[], queueId: string | undefined): number {
  const sorted = [...queues].sort((a, b) => a.sortOrder - b.sortOrder)
  const i = sorted.findIndex((q) => q.queueId === queueId)
  return i < 0 ? 1 : (i % TILE_COUNT) + 1
}

/** The Service fact's tile letter: the queue the ticket waits in now, which a transfer
 *  may have changed; the number's own letter only where that queue is unknown. */
export function serviceTileLetter(queue: Pick<QueueView, 'letter' | 'code'> | undefined, numberLetter: string): string {
  return queue ? queue.letter || queue.code.slice(0, 1) : numberLetter
}

export function LetterTile({ letter, tile, size }: { letter: string; tile: number; size?: 'sm' }) {
  return (
    <span className={size === 'sm' ? 'lt lt--sm' : 'lt'} data-tile={tile} aria-hidden="true">
      {letter}
    </span>
  )
}

/** "+994 50 ••• 43 80": the last four digits and the dialling prefix stay readable. */
export function maskPhone(value: string): string {
  const digits = value.replace(/\D/g, '')
  if (digits.length < 8) return value
  const head = digits.slice(0, digits.length - 7)
  const tail = digits.slice(-4)
  const grouped = head.length > 3 ? `${head.slice(0, 3)} ${head.slice(3)}` : head
  return `${value.trim().startsWith('+') ? '+' : ''}${grouped} ••• ${tail.slice(0, 2)} ${tail.slice(2)}`
}

/** The service a ticket is on now. */
export function serviceOfTicket(t: Pick<Ticket, 'transactions' | 'currentTransaction'>): string {
  return t.transactions[t.currentTransaction]?.serviceId ?? t.transactions[0]?.serviceId ?? ''
}

/** "A-020" from the letter and digits a ticket is drawn with. */
export function cardNumber(letter: string, digits: string, displayNumber: string): string {
  return letter && digits ? `${letter}-${digits}` : displayNumber
}

/** The desk's state: Ready and Serving green, Called the accent, a break or a late visit amber. */
export type Tone = 'ok' | 'call' | 'warn'

export function toneColor(tone: Tone): 'success' | 'accent' | 'warning' {
  return tone === 'call' ? 'accent' : tone === 'warn' ? 'warning' : 'success'
}

/** The state chip and the clock beside it, at the top of the stage. */
export function StageTop({ status, tone, aside }: { status: string; tone: Tone; aside?: ReactNode }) {
  return (
    <div className="cc-top">
      <Chip size="lg" variant="soft" color={toneColor(tone)} className="cc-status" data-tone={tone}>
        <i aria-hidden="true" />
        <Chip.Label>{status}</Chip.Label>
      </Chip>
      {aside && <span className="cc-aside">{aside}</span>}
    </div>
  )
}

/** The card's head: the state on top, then the number large with what they came for beside it. */
export function CardHead({
  number,
  muted,
  blink,
  service,
  chip,
  sub,
  status,
  tone,
  aside,
  marks,
}: {
  number: string
  muted?: boolean
  /** Alternates per blink so the animation restarts (styles/counter.css). */
  blink?: 'a' | 'b'
  service: string
  chip?: string
  sub: ReactNode
  status: string
  tone: Tone
  aside?: ReactNode
  /** The ticket's other marks (queue, transferred, booked), drawn after the priority chip. */
  marks?: ReactNode
}) {
  return (
    <>
      <StageTop status={status} tone={tone} aside={aside} />
      <div className="cc-head">
        {/* Keyed by the number: a new visit swaps in place, a recall changes nothing. */}
        <span className="cc-number" key={`n:${number}`} data-muted={muted || undefined} data-blink={blink}>
          {number}
        </span>
        <div className="cc-what" key={`w:${number}`}>
          <p className="cc-service">{service}</p>
          <p className="cc-sub">{sub}</p>
          {(chip || marks) && (
            <div className="cc-marks">
              {chip && (
                <Chip size="sm" variant="soft" color="warning">
                  {chip}
                </Chip>
              )}
              {marks}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

/** The priority's name, or '' for an ordinary ticket. */
export function priorityName(t: Pick<Ticket, 'priority'>, say: (k: 'common.vip' | 'common.priority') => string): string {
  return t.priority === 'vip' ? say('common.vip') : t.priority === 'standard' ? '' : say('common.priority')
}

/** What is known about the visit, as a grid of labelled cells. Priority is not
 *  one: the card's head carries it as a chip when it is not standard. */
export function FactsGrid({
  ticket,
  services,
  declared,
  letter,
  tile,
  waitedSec,
  held,
  bookedName,
  zone,
  onCustomer,
}: {
  ticket: Ticket
  services: readonly Service[]
  declared: readonly VisitField[]
  letter: string
  tile: number
  waitedSec: number
  /** waitedSec is time on hold (a held customer next up), not the wait to be called. */
  held?: boolean
  bookedName?: string
  zone?: string
  /** Opens Add or Edit customer; without it the customer is only shown. */
  onCustomer?: () => void
}) {
  const { lang, say } = useStaffText()
  const serviceId = serviceOfTicket(ticket)
  const cells: { key: string; label: string; value: ReactNode }[] = [
    {
      key: 'service',
      label: say('counter.fact.service'),
      value: (
        <>
          {letter && <LetterTile letter={letter} tile={tile} size="sm" />}
          {serviceLabel(services, serviceId, lang)}
        </>
      ),
    },
    // The card's head already says how long they waited; time on hold is new.
    ...(held ? [{ key: 'waited', label: say('counter.fact.onHold'), value: formatDuration(waitedSec, lang) }] : []),
    { key: 'taken', label: say('counter.fact.taken'), value: formatMoment(ticket.issuedAt, lang, { time: true, zone }) },
    { key: 'via', label: say('counter.fact.via'), value: say(`counter.source.${ticket.source}` as 'counter.source.kiosk') },
  ]
  if (ticket.language && isKnownLanguage(ticket.language)) {
    cells.push({ key: 'language', label: say('counter.fact.language'), value: languageName(ticket.language) })
  }
  const customer = ticket.customerName || bookedName
  if (customer || ticket.customerPhone || onCustomer) {
    cells.push({
      key: 'customer',
      label: say('counter.fact.customer'),
      value: (
        <>
          <span className="cc-facts__value">{[customer, ticket.customerPhone && maskPhone(ticket.customerPhone)].filter(Boolean).join(' · ')}</span>
          {onCustomer && (
            <Button variant="ghost" size="sm" className="cc-facts__edit" onPress={onCustomer}>
              {say(customer || ticket.customerPhone ? 'counter.customerEdit' : 'counter.customerAdd')}
            </Button>
          )}
        </>
      ),
    })
  }
  for (const f of counterFacts(declared, ticket.fields, lang)) {
    const phone = declared.find((d) => d.key === f.key)?.type === 'phone'
    cells.push({ key: `f:${f.key}`, label: f.label, value: phone ? maskPhone(f.value) : f.value })
  }
  return (
    <dl className="cc-facts">
      {cells.map((c) => (
        <div key={c.key}>
          <dt>{c.label}</dt>
          <dd>{c.value}</dd>
        </div>
      ))}
    </dl>
  )
}

/** What the card shows with nobody at the desk. Never the next ticket: Queue
 *  information beside it says who is next. */
export type FreeCard = 'paused' | 'standing' | 'ready' | 'empty'

export function freeCardKind(d: { paused: boolean; standing: boolean; waiting: number }): FreeCard {
  if (d.paused) return 'paused'
  if (d.standing) return 'standing'
  return d.waiting > 0 ? 'ready' : 'empty'
}

/** The free desk's card: the state chip, then a calm block with a heading, one line and the clock. */
export function ReadyCard({
  heading,
  line,
  pill,
  warn,
  icon,
  title,
  sub,
  late,
}: {
  heading: string
  line?: string
  pill: string
  warn?: boolean
  icon: ReactNode
  title?: string
  sub?: string
  late?: boolean
}) {
  return (
    <>
      <StageTop status={pill} tone={warn ? 'warn' : 'ok'} aside={sub ? <b data-late={late || undefined}>{sub}</b> : undefined} />
      <div className="cc-free">
        <span className="cc-free__icon" aria-hidden="true">{icon}</span>
        <div className="cc-free__text">
          <h2>{heading}</h2>
          {line && <p>{line}</p>}
          {title && <p className="cc-free__say">{title}</p>}
        </div>
      </div>
    </>
  )
}
